import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Map,
  MapPin,
  Navigation,
  ExternalLink,
  Clock,
  Ticket,
  Globe,
  Star,
  ChevronRight,
  Loader2,
  Route,
  Share2,
} from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const SharedRoadtrip = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roadtrip, setRoadtrip] = useState(null);

  useEffect(() => {
    fetchSharedRoadtrip();
  }, [shareId]);

  const fetchSharedRoadtrip = async () => {
    try {
      const response = await axios.get(`${API}/roadtrip/shared/${shareId}`);
      setRoadtrip(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("This shared roadtrip was not found.");
      } else if (err.response?.status === 410) {
        setError("This shared link has expired.");
      } else {
        setError("Failed to load shared roadtrip.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Build Google Maps directions URL
  const getGoogleMapsUrl = () => {
    if (!roadtrip?.destinations || roadtrip.destinations.length < 2) return "#";
    
    const dests = roadtrip.destinations;
    const origin = encodeURIComponent(dests[0].city + (dests[0].country ? `, ${dests[0].country}` : ""));
    const destination = encodeURIComponent(dests[dests.length - 1].city + (dests[dests.length - 1].country ? `, ${dests[dests.length - 1].country}` : ""));
    const waypoints = dests.slice(1, -1).map(d => 
      encodeURIComponent(d.city + (d.country ? `, ${d.country}` : ""))
    ).join("|");
    
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    if (waypoints) {
      url += `&waypoints=${waypoints}`;
    }
    return url;
  };

  // Build embed URL
  const getMapEmbedUrl = () => {
    if (!roadtrip?.destinations || roadtrip.destinations.length < 2) return null;
    
    const dests = roadtrip.destinations;
    const origin = encodeURIComponent(dests[0].city + (dests[0].country ? `, ${dests[0].country}` : ""));
    const destination = encodeURIComponent(dests[dests.length - 1].city + (dests[dests.length - 1].country ? `, ${dests[dests.length - 1].country}` : ""));
    
    if (dests.length === 2) {
      return `https://www.google.com/maps/embed/v1/directions?key=${GOOGLE_MAPS_API_KEY}&origin=${origin}&destination=${destination}&mode=driving`;
    }
    
    const waypoints = dests.slice(1, -1).map(d => 
      d.city + (d.country ? `, ${d.country}` : "")
    ).join("|");
    
    return `https://www.google.com/maps/embed/v1/directions?key=${GOOGLE_MAPS_API_KEY}&origin=${origin}&destination=${destination}&waypoints=${encodeURIComponent(waypoints)}&mode=driving`;
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container flex items-center justify-center p-4">
        <div className="glass-card p-8 text-center max-w-md">
          <Share2 className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Oops!</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate("/")} className="btn-primary">
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container pb-24" data-testid="shared-roadtrip-page">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
            <Share2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Shared Roadtrip</h1>
            <p className="text-sm text-slate-400">Someone shared this route with you</p>
          </div>
        </div>

        {/* Route Map */}
        {roadtrip?.destinations && roadtrip.destinations.length >= 2 && (
          <div className="glass-card p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-white flex items-center gap-2">
                <Map className="w-5 h-5 text-emerald-400" />
                Route
              </h2>
              <a
                href={getGoogleMapsUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-sky-400 hover:text-sky-300 flex items-center gap-1"
              >
                Open in Google Maps
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            
            <div className="h-64 md:h-80 rounded-xl overflow-hidden border border-white/10">
              {GOOGLE_MAPS_API_KEY ? (
                <iframe
                  title="Shared Route Map"
                  src={getMapEmbedUrl()}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-800/50">
                  <p className="text-slate-400">Map loading...</p>
                </div>
              )}
            </div>

            {/* Route Summary */}
            <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-2">
              {roadtrip.destinations.map((dest, idx) => (
                <div key={idx} className="flex items-center">
                  <div className={`px-3 py-2 rounded-lg flex items-center gap-2 flex-shrink-0 ${
                    idx === 0
                      ? "bg-emerald-500/20 text-emerald-400"
                      : idx === roadtrip.destinations.length - 1
                      ? "bg-rose-500/20 text-rose-400"
                      : "bg-slate-800/50 text-slate-300"
                  }`}>
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm whitespace-nowrap">{dest.city}</span>
                  </div>
                  {idx < roadtrip.destinations.length - 1 && (
                    <ChevronRight className="w-5 h-5 text-slate-600 mx-1 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sightseeing */}
        {roadtrip?.sights && roadtrip.sights.length > 0 && (
          <div className="glass-card p-4">
            <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-400" />
              Sightseeing Recommendations ({roadtrip.sights.length})
            </h2>
            
            <ScrollArea className="h-[400px]">
              <div className="space-y-4 pr-2">
                {roadtrip.sights.map((sight, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-semibold text-white truncate">{sight.name}</h3>
                          {sight.rating && (
                            <div className="flex items-center gap-1 text-amber-400 flex-shrink-0">
                              <Star className="w-4 h-4 fill-current" />
                              <span className="text-sm">{sight.rating}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-slate-400">{sight.location}</p>
                        <p className="text-sm text-slate-300 mt-1 line-clamp-2">{sight.description}</p>
                        
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                          {sight.opening_hours && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {sight.opening_hours}
                            </span>
                          )}
                          {sight.ticket_price && (
                            <span className="flex items-center gap-1">
                              <Ticket className="w-3 h-3" />
                              {sight.ticket_price}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* CTA */}
        <div className="glass-card p-6 mt-6 text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Plan Your Own Adventure</h3>
          <p className="text-slate-400 text-sm mb-4">
            Create your own roadtrip and share it with friends!
          </p>
          <Button onClick={() => navigate("/")} className="btn-primary">
            <Route className="w-4 h-4 mr-2" />
            Get Started
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SharedRoadtrip;
